/**
 * The end-of-level card.
 *
 * DESIGN.md §5: icon-heavy, because half the audience cannot fluently read a
 * results screen. Every row is ICON + NUMBER, the stars are the headline, and
 * the only words are three-syllable badge names a parent can read aloud once.
 *
 * Unearned badges are SHOWN, greyed, with the one-line hint that earns them.
 * Hiding them would mean a child never discovers Hurricane exists; showing them
 * as a visible gap is what turns a finished level into "let me try that again
 * without being seen".
 *
 * Plain DOM, no framework (AGENTS.md). Presentation only: it is handed a
 * ScoredResult and never computes one.
 */

import type { ScoredResult } from '@/systems/score';
import { formatDuration } from '@/systems/score';
import type { LevelRecord } from '@/systems/save';

export interface LevelCardView {
  levelName: string;
  scored: ScoredResult;
  /** The saved best BEFORE this run, or null on a first completion. */
  previousBest: LevelRecord | null;
  /** False when progress could not be written, so the card can say so. */
  saved: boolean;
}

export interface LevelCardActions {
  onPlayAgain: () => void;
  onLevels: () => void;
}

export class LevelCard {
  private readonly root: HTMLElement;
  private readonly starsRow: HTMLElement;
  private readonly title: HTMLElement;
  private readonly total: HTMLElement;
  private readonly best: HTMLElement;
  private readonly rows: HTMLElement;
  private readonly badgeRow: HTMLElement;
  private readonly again: HTMLButtonElement;
  private readonly levels: HTMLButtonElement;
  private visible = false;

  constructor(parent: HTMLElement, actions: LevelCardActions) {
    const host = document.createElement('div');
    host.id = 'level-card';
    host.innerHTML = TEMPLATE;
    parent.append(host);

    this.root = host;
    this.starsRow = mustFind(host, '#card-stars');
    this.title = mustFind(host, '#card-title');
    this.total = mustFind(host, '#card-total');
    this.best = mustFind(host, '#card-best');
    this.rows = mustFind(host, '#card-rows');
    this.badgeRow = mustFind(host, '#card-badges');
    this.again = mustFind(host, '#card-again') as HTMLButtonElement;
    this.levels = mustFind(host, '#card-levels') as HTMLButtonElement;

    this.again.addEventListener('click', actions.onPlayAgain);
    this.levels.addEventListener('click', actions.onLevels);
  }

  /**
   * Shows the card. Idempotent: the win check runs every step, so this is
   * called on every frame of a finished level.
   */
  show(view: LevelCardView): void {
    if (this.visible) return;
    this.visible = true;
    this.render(view);
    this.root.classList.add('visible');

    // The game holds pointer lock while playing, so without this the player
    // physically cannot click the button they are being shown.
    if (document.pointerLockElement) document.exitPointerLock();
    // Focus so Enter and Space work for a player who never touches the mouse.
    this.again.focus();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.root.classList.remove('visible');
  }

  get isVisible(): boolean {
    return this.visible;
  }

  private render(view: LevelCardView): void {
    const { scored, previousBest } = view;

    this.title.textContent = view.levelName;
    this.starsRow.textContent = '★★★'.slice(0, scored.stars) + '☆☆☆'.slice(0, 3 - scored.stars);
    this.starsRow.dataset.stars = String(scored.stars);
    this.total.textContent = String(scored.total);

    const beatenBest = previousBest !== null && scored.total > previousBest.bestTotal;
    if (previousBest === null || beatenBest) {
      this.best.textContent = 'New best!';
      this.best.classList.add('new');
    } else {
      this.best.textContent = `Best ${previousBest.bestTotal}`;
      this.best.classList.remove('new');
    }

    this.rows.innerHTML = '';
    this.addRow('💥', 'Chaos', String(scored.chaos));
    if (scored.bonus > 0) this.addRow('🎖️', 'Badge bonus', `+${scored.bonus}`);
    this.addRow('😻', 'Cozy', String(scored.result.cozy));
    // Out of the level's TOTAL, not out of what was required: you only need two
    // of three to finish (DESIGN.md §5), so counting against the requirement
    // would print "3/2" at a child who found all of them.
    this.addRow('🔑', 'Keys', `${scored.result.keysCollected}/${scored.result.keysTotal}`);
    this.addRow('⏱️', 'Time', formatDuration(scored.result.elapsedSeconds));
    if (!view.saved) this.addRow('⚠️', 'Not saved', 'no storage');

    this.badgeRow.innerHTML = '';
    for (const badge of scored.badges) {
      const chip = document.createElement('div');
      chip.className = badge.earned ? 'badge-chip earned' : 'badge-chip';
      // The hint is the whole reason unearned badges are shown at all.
      chip.title = badge.hint;

      const icon = document.createElement('span');
      icon.className = 'badge-icon';
      icon.textContent = badge.icon;

      const label = document.createElement('span');
      label.className = 'badge-label';
      label.textContent = badge.earned ? badge.label : badge.hint;

      chip.append(icon, label);
      this.badgeRow.append(chip);
    }
  }

  private addRow(icon: string, label: string, value: string): void {
    const row = document.createElement('li');

    const iconEl = document.createElement('span');
    iconEl.className = 'row-icon';
    iconEl.textContent = icon;

    const labelEl = document.createElement('span');
    labelEl.className = 'row-label';
    labelEl.textContent = label;

    const valueEl = document.createElement('span');
    valueEl.className = 'row-value';
    valueEl.textContent = value;

    row.append(iconEl, labelEl, valueEl);
    this.rows.append(row);
  }

  dispose(): void {
    this.root.remove();
  }
}

function mustFind(root: HTMLElement, selector: string): HTMLElement {
  const element = root.querySelector<HTMLElement>(selector);
  if (!element) throw new Error(`Level card template is missing ${selector}`);
  return element;
}

const TEMPLATE = `
  <div class="card-panel" role="dialog" aria-label="Level complete">
    <div id="card-stars" class="card-stars">☆☆☆</div>
    <h2 id="card-title">Level</h2>
    <div class="card-total-row">
      <span id="card-total" class="card-total">0</span>
      <span id="card-best" class="card-best"></span>
    </div>
    <ul id="card-rows" class="card-rows"></ul>
    <div id="card-badges" class="card-badges"></div>
    <div class="card-actions">
      <button id="card-again" class="card-button" type="button">🐾 Play again</button>
      <button id="card-levels" class="card-button secondary" type="button">🗺️ Levels</button>
    </div>
  </div>
`;
